import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { UserProfile } from '../../types/soul.js';
import * as soulApi from '../../services/soul-api.js';

const TIMEZONE_OPTIONS = [
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Zurich',
  'Europe/Vienna',
  'Europe/Amsterdam',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
  'UTC',
];

const LANGUAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'English' },
];

@customElement('cp-user-profile-editor')
export class CpUserProfileEditor extends LitElement {
  createRenderRoot() { return this; }

  @state() private _loading = true;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _successMessage = '';

  // Profile fields
  @state() private _displayName = '';
  @state() private _timezone = 'Europe/Berlin';
  @state() private _language = 'de';
  @state() private _lifeContext = '';
  @state() private _agentNameOverride = '';

  connectedCallback(): void {
    super.connectedCallback();
    this._loadProfile();
  }

  private async _loadProfile(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const profile = await soulApi.getUserProfile();
      this._applyProfile(profile);
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Laden des Profils';
    } finally {
      this._loading = false;
    }
  }

  private _applyProfile(profile: UserProfile): void {
    this._displayName = profile.display_name ?? '';
    this._timezone = profile.timezone ?? 'Europe/Berlin';
    this._language = profile.language ?? 'de';
    this._lifeContext = profile.life_context ?? '';
    this._agentNameOverride = profile.agent_name_override ?? '';
  }

  private _onDisplayNameInput(e: Event): void {
    this._displayName = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private _onTimezoneChange(e: Event): void {
    this._timezone = (e.target as HTMLSelectElement).value;
    this._successMessage = '';
  }

  private _onLanguageChange(e: Event): void {
    this._language = (e.target as HTMLSelectElement).value;
    this._successMessage = '';
  }

  private _onLifeContextInput(e: Event): void {
    this._lifeContext = (e.target as HTMLTextAreaElement).value;
    this._successMessage = '';
  }

  private _onAgentNameOverrideInput(e: Event): void {
    this._agentNameOverride = (e.target as HTMLInputElement).value;
    this._successMessage = '';
  }

  private async _save(): Promise<void> {
    if (this._saving) return;

    this._saving = true;
    this._error = '';
    this._successMessage = '';

    try {
      const profile = await soulApi.updateUserProfile({
        display_name: this._displayName,
        timezone: this._timezone,
        language: this._language,
        life_context: this._lifeContext || null,
        agent_name_override: this._agentNameOverride || null,
      });

      this._applyProfile(profile);
      this._successMessage = 'Profil gespeichert';
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Fehler beim Speichern';
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      <div class="cp-user-profile-editor">
        <h3 class="cp-user-profile-editor__title">User-Profil</h3>

        ${this._error ? html`
          <div class="cp-user-profile-editor__error">${this._error}</div>
        ` : nothing}

        ${this._successMessage ? html`
          <div class="cp-user-profile-editor__success">${this._successMessage}</div>
        ` : nothing}

        ${this._loading ? html`
          <p class="cp-user-profile-editor__loading">Profil wird geladen...</p>
        ` : html`
          <div class="cp-user-profile-editor__form">
            <div class="cp-user-profile-editor__field">
              <label class="cp-user-profile-editor__label" for="profile-display-name">Display-Name</label>
              <input
                id="profile-display-name"
                class="cp-user-profile-editor__input"
                type="text"
                .value=${this._displayName}
                @input=${this._onDisplayNameInput}
                placeholder="Dein Name"
              />
            </div>

            <div class="cp-user-profile-editor__field">
              <label class="cp-user-profile-editor__label" for="profile-timezone">Timezone</label>
              <select
                id="profile-timezone"
                class="cp-user-profile-editor__select"
                @change=${this._onTimezoneChange}
              >
                ${TIMEZONE_OPTIONS.map(tz => html`
                  <option value=${tz} ?selected=${this._timezone === tz}>${tz}</option>
                `)}
              </select>
            </div>

            <div class="cp-user-profile-editor__field">
              <label class="cp-user-profile-editor__label" for="profile-language">Sprache</label>
              <select
                id="profile-language"
                class="cp-user-profile-editor__select"
                @change=${this._onLanguageChange}
              >
                ${LANGUAGE_OPTIONS.map(lang => html`
                  <option value=${lang.value} ?selected=${this._language === lang.value}>${lang.label}</option>
                `)}
              </select>
            </div>

            <div class="cp-user-profile-editor__field">
              <label class="cp-user-profile-editor__label" for="profile-life-context">Life-Context</label>
              <textarea
                id="profile-life-context"
                class="cp-user-profile-editor__textarea"
                rows="4"
                .value=${this._lifeContext}
                @input=${this._onLifeContextInput}
                placeholder="Aktueller Fokus, Situation, Kontext..."
              ></textarea>
            </div>

            <div class="cp-user-profile-editor__field">
              <label class="cp-user-profile-editor__label" for="profile-agent-name">Agent-Name (Override)</label>
              <input
                id="profile-agent-name"
                class="cp-user-profile-editor__input"
                type="text"
                .value=${this._agentNameOverride}
                @input=${this._onAgentNameOverrideInput}
                placeholder="Leer = Standard-Name aus Soul"
              />
              <span class="cp-user-profile-editor__hint">Leer lassen, um den Standard-Agent-Namen aus der Soul-Konfiguration zu verwenden.</span>
            </div>
          </div>

          <button
            class="cp-user-profile-editor__btn cp-user-profile-editor__btn--save"
            @click=${this._save}
            ?disabled=${this._saving}
          >${this._saving ? 'Speichert...' : 'Speichern'}</button>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-user-profile-editor': CpUserProfileEditor;
  }
}
